/**
 * Helper functions for TaskList component logic
 */

import { TASK_LIST_URL_PARAMS as URLParams } from '../constants/constants';

/**
 * Determines whether the task list is in "all locations" mode.  The flag arrives
 * either as a real boolean (from redux) or as the string 'true' (from URL params),
 * so both have to be accepted.
 * @param {Object} searchParams - The search parameters object
 * @returns {boolean} True when the list should span every accessible location
 */
export const isAllLocationsMode = (searchParams) =>
    searchParams?.allLocations === true || searchParams?.allLocations === 'true';

/**
 * Determines the locationKeys array for loadColumnFilters based on search parameters
 * @param {Object} searchParams - The search parameters object
 * @param {string|null} defaultLocationKey - The default location key to fall back to
 * @returns {string[]} Array of location keys to use for filtering
 */
export const determineLocationKeysForFilters = (searchParams, defaultLocationKey) => {
    // Properly handle boolean values from URL parameters (strings vs booleans)
    const isAllLocations = isAllLocationsMode(searchParams);

    let locationKeys = Array.isArray(searchParams?.locationKeys) 
        ? searchParams.locationKeys 
        : [];

    // If not in "all locations" mode and no location keys selected, fall back to default
    if (!isAllLocations && locationKeys.length === 0) {
        locationKeys = defaultLocationKey ? [defaultLocationKey] : [];
    }

    // When in "all locations" mode, return empty array (loads all locations)
    if (isAllLocations) {
        locationKeys = [];
    }

    return locationKeys;
};

/**
 * Builds the task list's browser query string from a set of search params, mapping
 * each param to its short URL alias.
 * @param {Object} searchParams - The search parameters to serialize
 * @param {string} [changedKey] - The search param the user just changed, if any
 * @returns {string} The query string, without a leading '?'
 */
export const buildTaskListQueryString = (searchParams, changedKey) =>
    Object.entries(searchParams || {})
        .filter(([paramKey, value]) => {
            const alias = URLParams[paramKey.charAt(0).toUpperCase() + paramKey.slice(1)]; // Map key to URL param alias
            
            if (alias === URLParams.AllLocations) return isAllLocationsMode(searchParams);

            if (alias === URLParams.LocationKeys)
                return changedKey === 'locationKeys' || (Array.isArray(value) && value.length > 0);

            return alias && value !== null && value !== undefined && (Array.isArray(value) ? value.length > 0 : value !== '');
        })
        .flatMap(([paramKey, value]) => {
            const alias = URLParams[paramKey.charAt(0).toUpperCase() + paramKey.slice(1)];
            return Array.isArray(value)
                ? value.map((v) => `${alias}=${encodeURIComponent(v)}`) // For arrays, multiple key-value pairs
                : `${alias}=${encodeURIComponent(value)}`; // For non-arrays, single key-value pair
        })
        .join('&');